import React, { createContext, useContext } from 'react';
import { useStore } from 'react-redux';
import { Dropdown, Icon, Menu, Typography } from 'antd';
import cn from 'classnames';
import { formatPhoneNumber } from '../../helpers/formatPhoneNumber';
import useSipStates from '../../hooks/useSipStates';
import { SipPhoneContext } from '../SipPhoneProvider';
import {
  setCallDirection,
  setIsControlPanelVisible,
  setSubscriber,
} from '../../containers/Phone/store/actions';
import { callDirections } from '../../containers/Phone/constants/callDirections';
import { isControlPanelVisibleSelector } from '../../containers/Phone/store/selectors';
import { ReactComponent as PhoneIcon } from '../../resource/HandSetAdaptiveIcon.svg';
import styles from './index.module.scss';

const { Text } = Typography;

const CallAvailableContext = createContext();

const defaultFormatNumber = (number) =>
  formatPhoneNumber({
    phoneNumber: number,
    format: '(xxx) xxx-xx-xx',
    placeholder: 'x',
  });

const PhoneBlock = ({
  rootClassName,
  name,
  number,
  formatNumber = defaultFormatNumber,
  iconConfig,
}) => {
  return (
    <CallAvailableProvider>
      <Wrapper name={name} number={number}>
        <Content
          rootClassName={rootClassName}
          iconConfig={iconConfig}
          number={formatNumber(number)}
        />
      </Wrapper>
    </CallAvailableProvider>
  );
};

export default PhoneBlock;

function CallAvailableProvider({ children }) {
  const { isConnected, isIdle } = useSipStates();

  const isCallAvailable = isConnected && isIdle;

  return (
    <CallAvailableContext.Provider value={isCallAvailable}>
      {children}
    </CallAvailableContext.Provider>
  );
}

function Wrapper({ children, name, number }) {
  const isCallAvailable = useContext(CallAvailableContext);

  if (!isCallAvailable) {
    return <>{children}</>;
  }

  return (
    <CallDropdown name={name} number={number}>
      {children}
    </CallDropdown>
  );
}

function CallDropdown({ children, name, number }) {
  const { getState, dispatch } = useStore();

  const { startCall } = useContext(SipPhoneContext);

  const handleClick = ({ key }) => {
    if (key === 'call') {
      if (!isControlPanelVisibleSelector(getState())) {
        dispatch(setIsControlPanelVisible(true));
      }

      dispatch(setCallDirection(callDirections.outgoing));

      const subscriber = { phone: number };

      if (name) {
        subscriber.serviceCompany = name;
      }

      dispatch(setSubscriber(subscriber));
      startCall(formatPhoneNumber({ phoneNumber: number }));
    }
  };

  const menu = (
    <Menu onClick={handleClick}>
      <Menu.Item key="call">Позвонить</Menu.Item>
    </Menu>
  );

  return (
    <Dropdown
      overlay={menu}
      getPopupContainer={(triggerNode) => triggerNode.parentNode}
    >
      {children}
    </Dropdown>
  );
}

function Content({ rootClassName, iconConfig, number, ...props }) {
  const isCallAvailable = useContext(CallAvailableContext);
  const hasConfig = iconConfig != null && typeof iconConfig === 'object';

  const iconPos = iconConfig?.position ?? 'left';

  return (
    <div
      {...props}
      className={cn(styles.root, rootClassName, {
        [styles.interactive]: isCallAvailable,
      })}
    >
      {hasConfig && iconPos === 'left' && (
        <IconComponent
          component={iconConfig.component}
          className={iconConfig.className}
        />
      )}

      <Text>{number}</Text>

      {hasConfig && iconPos === 'right' && (
        <IconComponent
          component={iconConfig.component}
          className={iconConfig.className}
        />
      )}
    </div>
  );
}

function IconComponent({ component, className }) {
  return <Icon component={component ?? DefaultIcon} className={className} />;
}

function DefaultIcon() {
  return <PhoneIcon className={styles.icon} />;
}
